import { Button } from '@/components/ui/button';
import { ArrowRight, Workflow, Zap } from 'lucide-react';

interface HeroSectionProps {
  onGetStarted: () => void;
}

const HeroSection = ({ onGetStarted }: HeroSectionProps) => {
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-n8n-gradient opacity-90" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-electric-400/20 rounded-full blur-3xl animate-bounce-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-white/10 rounded-full blur-2xl animate-pulse" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-6 max-w-5xl mx-auto">
        <div className="animate-fade-in">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Workflow className="w-8 h-8 text-electric-400" />
            <span className="text-electric-400 font-semibold text-lg">n8n Automation Generator</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Build{' '}
            <span className="text-gradient bg-gradient-to-r from-electric-400 to-white bg-clip-text text-transparent">
              n8n Workflows
            </span>
            <br />
            with Simple Prompts
          </h1>
          
          <p className="text-xl md:text-2xl text-n8n-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Transform your automation ideas into ready-to-use n8n workflows. 
            Just describe what you want to automate, and our AI will generate the complete workflow JSON for you.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button 
              onClick={onGetStarted}
              size="lg" 
              className="bg-white text-n8n-700 hover:bg-n8n-50 px-8 py-4 text-lg font-semibold h-auto group transition-all duration-200 transform hover:scale-105"
            >
              Start Generating
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <Button 
              variant="outline" 
              size="lg"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg h-auto backdrop-blur-sm"
            >
              <Zap className="mr-2 w-5 h-5" />
              View Examples
            </Button>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                <Workflow className="w-8 h-8 text-electric-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Natural Language Input</h3>
              <p className="text-n8n-100">Describe your automation in plain English</p>
            </div>
            
            <div className="text-center animate-slide-up" style={{ animationDelay: '0.4s' }}>
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                <Zap className="w-8 h-8 text-electric-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Instant Generation</h3>
              <p className="text-n8n-100">Get ready-to-use n8n JSON workflows</p>
            </div>
            
            <div className="text-center animate-slide-up" style={{ animationDelay: '0.6s' }}>
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                <ArrowRight className="w-8 h-8 text-electric-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">One-Click Import</h3>
              <p className="text-n8n-100">Download and import directly into n8n</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;